// El tablero en vivo del onboarding de marca (ADR-2026-09-28-onboarding-v2-etapa-1):
// qué pasos muestra la línea de análisis, cuándo se considera disponible cada uno y el
// "director de ritmo", que los revela de a uno aunque el backend los entregue juntos.
// Todo puro: la pantalla lo llama con el estado del escaneo y la hora.

import type { EscaneoDeMarca } from './escaneo';
import { RUBRO_OTRO } from './rubroOtro';
import type { RespuestasDeLaEspera } from './marca';

export type PasoId = 'sitio' | 'logo' | 'captura' | 'colores' | 'tipografia' | 'tono' | 'espacio';

export interface PasoDelAnalisis {
  id: PasoId;
  /** Verbo + objeto, en curso. `{dominio}` se reemplaza. */
  texto: string;
  /** De dónde sale, chiquito y en gris. */
  detalle: string;
}

export const PASOS_DEL_ANALISIS: readonly PasoDelAnalisis[] = [
  { id: 'sitio', texto: 'Entrando a {dominio}', detalle: 'título, descripción y links' },
  { id: 'logo', texto: 'Encontrando tu logo', detalle: 'el ícono de tu sitio' },
  { id: 'captura', texto: 'Mirando tu home', detalle: 'una captura de la página' },
  { id: 'colores', texto: 'Analizando tus colores', detalle: 'y para qué se usa cada uno' },
  { id: 'tipografia', texto: 'Reconociendo tu tipografía', detalle: 'el estilo de tus textos' },
  { id: 'tono', texto: 'Entendiendo tu forma de comunicar', detalle: 'tu público y tu tono' },
  { id: 'espacio', texto: 'Preparando tu espacio', detalle: 'guardando tu marca' },
];

/**
 * Sin link ("Ahora no"): la persona cuenta lo básico y corre la rama MANUAL del mismo
 * análisis, que arma paleta, tipografía y tono con eso. Mismos ids de tarjeta, sin
 * captura (ADR-2026-09-29-onboarding-v2-camino-manual).
 */
export const PASOS_MANUALES: readonly PasoDelAnalisis[] = [
  { id: 'sitio', texto: 'Revisando los datos de tu marca', detalle: 'nombre, rubro y productos' },
  { id: 'logo', texto: 'Mirando tu logo', detalle: 'su forma y sus colores' },
  { id: 'colores', texto: 'Armando tu paleta', detalle: 'con tu logo y tu rubro' },
  { id: 'tipografia', texto: 'Eligiendo tu tipografía', detalle: 'la que va con tu marca' },
  { id: 'tono', texto: 'Definiendo tu forma de comunicar', detalle: 'tu público y tu tono' },
  { id: 'espacio', texto: 'Preparando tu espacio', detalle: 'guardando tu marca' },
];

/** De dónde sale la marca: el link de su web, o lo que la persona cuenta a mano. */
export type OrigenDeLaMarca = 'web' | 'manual';

export function pasosDelOrigen(origen: OrigenDeLaMarca): readonly PasoDelAnalisis[] {
  return origen === 'manual' ? PASOS_MANUALES : PASOS_DEL_ANALISIS;
}

const ORDEN: readonly PasoId[] = PASOS_DEL_ANALISIS.map((p) => p.id);

/** Cuánto queda a la vista cada hallazgo, como mínimo, antes de revelar el siguiente. */
export const MIN_MS_POR_HALLAZGO = 900;

const terminado = (e: EscaneoDeMarca) => e.estado === 'listo' || e.estado === 'fallo';
const pasoHecho = (e: EscaneoDeMarca, nombre: string) => e.pasos.some((p) => p.nombre === nombre);

/**
 * Los pasos que ya se pueden revelar: llegó su dato, o el backend cerró ese paso sin
 * dato (se revela igual y la tarjeta queda en "completalo vos"), o el escaneo terminó.
 */
export function pasosDisponibles(e: EscaneoDeMarca, origen: OrigenDeLaMarca = 'web'): Set<PasoId> {
  const h = e.hallazgos;
  const fin = terminado(e);
  const out = new Set<PasoId>();
  if (origen === 'manual') {
    // Lo que contó la persona ya está: se "lee" apenas arranca el análisis. El logo, cuando
    // el backend lo describió (o si no subió ninguno, cuando llega la identidad).
    if (e.estado !== 'en_cola' || fin) out.add('sitio');
    if (h.logo || pasoHecho(e, 'analizar_logo') || h.identidad || fin) out.add('logo');
    if (h.identidad || fin) {
      out.add('colores');
      out.add('tipografia');
      out.add('tono');
    }
    if (fin) out.add('espacio');
    return out;
  }
  if (h.sitio || pasoHecho(e, 'scrape_web') || fin) out.add('sitio');
  if (h.logo || pasoHecho(e, 'guardar_logo_web') || pasoHecho(e, 'screenshot_web') || fin) out.add('logo');
  if (h.captura || pasoHecho(e, 'screenshot_web') || fin) out.add('captura');
  if (h.identidad || fin) {
    out.add('colores');
    out.add('tipografia');
    out.add('tono');
  }
  if (fin) out.add('espacio');
  return out;
}

/**
 * El director de ritmo: el próximo paso a revelar, EN ORDEN, si ya está disponible y
 * el anterior estuvo a la vista al menos `minMs`. null si todavía no toca.
 */
export function siguienteRevelado(p: {
  disponibles: Set<PasoId>;
  revelados: readonly PasoId[];
  ultimoReveladoMs: number | null;
  ahoraMs: number;
  minMs?: number;
  /** El orden de los pasos: el de la web por defecto, `PASOS_MANUALES` sin link. */
  orden?: readonly PasoId[];
}): PasoId | null {
  const siguiente = (p.orden ?? ORDEN).find((id) => !p.revelados.includes(id));
  if (!siguiente || !p.disponibles.has(siguiente)) return null;
  const min = p.minMs ?? MIN_MS_POR_HALLAZGO;
  if (p.ultimoReveladoMs !== null && p.ahoraMs - p.ultimoReveladoMs < min) return null;
  return siguiente;
}

/** El paso que la línea muestra "en curso": el primero sin revelar. */
export function pasoEnCurso(revelados: readonly PasoId[], orden: readonly PasoId[] = ORDEN): PasoId | null {
  return orden.find((id) => !revelados.includes(id)) ?? null;
}

/** Revelado todo lo que importa para seguir: logo + colores (el resto se puede completar). */
export function puedeSeguir(revelados: readonly PasoId[]): boolean {
  return revelados.includes('espacio');
}

/** Las preguntas de la espera: obligatorias (ADR-2026-09-29-onboarding-v2-accion-abajo). */
export const PREGUNTAS_OBLIGATORIAS = ['primero', 'rol', 'anunciosPagos', 'canal'] as const satisfies ReadonlyArray<
  keyof RespuestasDeLaEspera
>;

/** `yaGenero`: no se le pregunta qué crear primero (ADR-2026-10-31-las-primeras-piezas-solo-si-no-genero). */
export function preguntasSinResponder(r: RespuestasDeLaEspera, yaGenero = false): number {
  return PREGUNTAS_OBLIGATORIAS.filter((clave) => !(yaGenero && clave === 'primero') && !r[clave]).length;
}

/**
 * Por qué "Todo bien, sigamos" todavía no se puede tocar, dicho al lado del botón. Null
 * mientras analiza (ahí habla el estado del análisis) o cuando no falta nada de la persona.
 */
export function porQueNoSigue(p: {
  terminado: boolean;
  respuestas: RespuestasDeLaEspera;
  nombre: string;
  rubro: string;
  /** Con "Otro": cuál es, en sus palabras (ADR-2026-10-12-el-rubro-otro-se-cuenta-y-se-ajusta). */
  rubroDetalle?: string;
  /** Una marca nueva desde adentro de la app: las preguntas ya se respondieron con la primera. */
  sinPreguntas?: boolean;
  /** Ya generó: sin la pregunta de qué crear primero. */
  yaGenero?: boolean;
}): { motivo: 'preguntas' | 'nombre' | 'rubro'; texto: string } | null {
  if (!p.terminado) return null;
  const faltan = p.sinPreguntas ? 0 : preguntasSinResponder(p.respuestas, p.yaGenero);
  if (faltan > 0) {
    return {
      motivo: 'preguntas',
      texto: faltan === 1 ? 'Responde la pregunta de arriba para seguir.' : `Responde las ${faltan} preguntas de arriba para seguir.`,
    };
  }
  if (!p.nombre.trim()) return { motivo: 'nombre', texto: 'Escribe el nombre de tu marca para seguir.' };
  if (!p.rubro) return { motivo: 'rubro', texto: 'Elige tu rubro para seguir: con él te mostramos los modos que te sirven.' };
  if (p.rubro === RUBRO_OTRO && !p.rubroDetalle?.trim()) {
    return { motivo: 'rubro', texto: 'Escribe cuál es tu rubro para seguir.' };
  }
  return null;
}

/**
 * Las secciones del análisis (markdown con `### N. Título`), por número. El prompt del
 * backend fija 5: identidad, público, esencia visual, tono, posicionamiento.
 */
export function seccionesDelAnalisis(analisis: string): Record<number, string> {
  const out: Record<number, string> = {};
  const partes = analisis.split(/^#{2,4}\s*(\d+)\.[^\n]*\n/m);
  // split con grupo: [antes, n1, cuerpo1, n2, cuerpo2, ...]
  for (let i = 1; i + 1 < partes.length; i += 2) {
    const n = Number(partes[i]);
    const cuerpo = (partes[i + 1] ?? '').trim();
    if (Number.isFinite(n) && cuerpo) out[n] = cuerpo;
  }
  return out;
}

/** Las primeras `n` oraciones de un texto, sin markdown. */
export function primerasOraciones(texto: string, n = 2): string {
  const plano = texto.replace(/[*_`#>]/g, '').replace(/\s+/g, ' ').trim();
  const oraciones = plano.match(/[^.!?]+[.!?]+/g) ?? [plano];
  return oraciones
    .slice(0, n)
    .map((o) => o.trim())
    .join(' ');
}

// ─── El tablero con Cumbre (ADR-2026-10-02-onboarding-v2-tablero-con-cumbre) ─────────

/**
 * Cómo se nombra cada parte en la sección de lo que falta analizar: todas juntas, en una sola sección,
 * sin explicar qué hace cada una.
 */
export function etiquetaDelPaso(id: PasoId, origen: OrigenDeLaMarca): string {
  switch (id) {
    case 'sitio':
      return origen === 'manual' ? 'Datos de tu marca' : 'Tu sitio';
    case 'logo':
      return 'Logo';
    case 'captura':
      return 'Captura de tu home';
    case 'colores':
      return 'Colores';
    case 'tipografia':
      return 'Tipografía';
    case 'tono':
      return 'Forma de comunicar';
    case 'espacio':
      return 'Guardado de tu marca';
  }
}

/** Lo que todavía no se reveló, en el orden del análisis. */
export function pasosPendientes(orden: readonly PasoId[], revelados: readonly PasoId[]): PasoId[] {
  return orden.filter((id) => !revelados.includes(id));
}

/**
 * En qué está el tablero. `contando`: sin link, la persona todavía no armó su marca.
 * `incompleto`: el análisis terminó con error o se demoró, y hay cosas para completar.
 */
export type EstadoDelTablero = 'contando' | 'analizando' | 'listo' | 'incompleto';

export function estadoDelTablero(p: {
  contando: boolean;
  terminado: boolean;
  fallo: boolean;
  demorado: boolean;
}): EstadoDelTablero {
  if (p.contando) return 'contando';
  if (!p.terminado) return 'analizando';
  return p.fallo || p.demorado ? 'incompleto' : 'listo';
}
